import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  Storefront,
  MapPin,
  CalendarBlank,
  Sparkle,
  PaperPlaneTilt,
  CircleNotch,
  ShareNetwork,
} from "@phosphor-icons/react";

/**
 * FindATruck — Event Inquiry marketplace, multi-step host form.
 *
 * Single page, 4-step linear stepper. Backend `/find-trucks/match` runs
 * after Step 2 (cuisine + date + zip), then host picks trucks in Step 3,
 * fills contact in Step 4, submits to `/event-inquiries`.
 *
 * Phase 1: host email + Reply-To-routed responses. Owner inbox (Phase 2)
 * lights up the dashboard tile after this ships.
 */

const EVENT_TYPES = [
  { value: "wedding",        label: "Wedding" },
  { value: "corporate",      label: "Corporate" },
  { value: "business lunch", label: "Business lunch" },
  { value: "birthday",       label: "Birthday" },
  { value: "family party",   label: "Family party" },
  { value: "festival",       label: "Festival" },
  { value: "graduation",     label: "Graduation" },
  { value: "school event",   label: "School event" },
  { value: "private",        label: "Private party" },
  { value: "other",          label: "Other" },
];

const COMMON_CUISINES = [
  "tacos", "bbq", "pizza", "burgers", "coffee", "ice cream",
  "asian", "mexican", "italian", "vegan", "desserts", "breakfast",
];

/**
 * ShareInquiryButton — pulls the share URL from the backend (which
 * resolves _public_url() based on prod vs preview) and triggers
 * navigator.share when available, otherwise copies to clipboard.
 *
 * The shared URL is `/api/event-marketplace/share` — server-rendered
 * OG-tagged HTML so iMessage/Facebook/etc. unfurl the big orange
 * preview card. Real users land on /find-a-truck via redirect.
 */
function ShareInquiryButton() {
  const [busy, setBusy] = useState(false);
  const handleShare = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/event-marketplace/share.json");
      const url = data.share_url;
      const title = data.title || "Need a food truck for your event?";
      const text  = data.description || "";
      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
          return;
        } catch {
          /* user cancelled — fall through to copy */
        }
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied", {
          description: "Share it anywhere — the preview shows the hero card.",
        });
      } else {
        // very-old-browser fallback
        window.prompt("Copy this link", url);
      }
    } catch (e) {
      toast.error("Couldn't get the share link. Try again?");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={busy}
      className="mt-4 inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 transition disabled:opacity-50"
      data-testid="find-a-truck-share-btn"
    >
      {busy ? (
        <CircleNotch weight="bold" className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <ShareNetwork weight="bold" className="h-3.5 w-3.5" />
      )}
      Share this with a co-organizer
    </button>
  );
}


function StepHeader({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="find-a-truck-stepper">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className="flex items-center gap-2 flex-1">
          <div
            className={
              "h-7 w-7 rounded-full grid place-items-center text-xs font-semibold transition-colors " +
              (n < step
                ? "bg-primary text-primary-foreground"
                : n === step
                ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                : "bg-muted text-muted-foreground")
            }
          >
            {n < step ? <CheckCircle weight="fill" className="h-4 w-4" /> : n}
          </div>
          {n < 4 && (
            <div className={"flex-1 h-0.5 " + (n < step ? "bg-primary" : "bg-muted")} />
          )}
        </div>
      ))}
    </div>
  );
}

function TruckCard({ truck, checked, onToggle }) {
  return (
    <label
      className={
        "flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition " +
        (checked
          ? "border-primary bg-primary/5"
          : "border-border bg-card hover:border-primary/40")
      }
      data-testid={`match-truck-${truck.truck_id}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(truck.truck_id)}
        className="mt-1.5 h-4 w-4 accent-primary flex-none"
        data-testid={`match-truck-${truck.truck_id}-checkbox`}
      />
      <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted grid place-items-center flex-none">
        {truck.logo_url ? (
          <img src={truck.logo_url} alt={truck.truck_name} className="h-full w-full object-cover" />
        ) : (
          <Storefront weight="duotone" className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">{truck.truck_name}</span>
          {truck.is_pro && (
            <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded uppercase tracking-wide">
              Pro
            </span>
          )}
          {truck.distance_miles != null && (
            <span className="text-[11px] text-muted-foreground">
              {truck.distance_miles}mi
            </span>
          )}
        </div>
        {truck.blurb && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{truck.blurb}</p>
        )}
        {truck.matched_cuisines && truck.matched_cuisines.length > 0 && (
          <div
            className="mt-1.5 flex flex-wrap items-center gap-1"
            data-testid={`match-truck-${truck.truck_id}-matched-cuisines`}
          >
            <span className="text-[9px] font-semibold uppercase tracking-wider text-primary/80">
              Matched
            </span>
            {truck.matched_cuisines.slice(0, 4).map((c) => (
              <span
                key={c}
                className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full ring-1 ring-primary/20 font-medium"
              >
                {c}
              </span>
            ))}
          </div>
        )}
        {truck.cuisine_tags && truck.cuisine_tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {truck.cuisine_tags.slice(0, 4).map((t) => (
              <span key={t} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </label>
  );
}

export default function FindATruck() {
  const [step, setStep] = useState(1);

  // ----- Step 1 fields -----
  const [eventDate, setEventDate] = useState("");
  const [eventZip, setEventZip] = useState("");
  const [partySize, setPartySize] = useState(20);

  // ----- Step 2 fields -----
  const [eventType, setEventType] = useState("wedding");
  const [cuisines, setCuisines] = useState([]);  // array of tags
  const [anyCuisine, setAnyCuisine] = useState(false);
  const [budget, setBudget] = useState("");

  // ----- Step 3 fields -----
  const [matches, setMatches] = useState([]);
  const [alsoNearby, setAlsoNearby] = useState([]);
  const [showAlsoNearby, setShowAlsoNearby] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [geocoded, setGeocoded] = useState(true);
  // "Show Pro trucks only" filter — verified businesses surface higher
  // for higher-stakes events (weddings, corporate). When toggled on we
  // also auto-uncheck any free trucks already in the selection so the
  // summary on step 4 doesn't lie.
  const [proOnly, setProOnly] = useState(false);

  // ----- Step 4 fields -----
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");  // honeypot
  const [submitting, setSubmitting] = useState(false);

  // Submitted state
  const [submitted, setSubmitted] = useState(null);  // {inquiry_id, sent_count, view_url, sent_truck_names}

  // ---------- Step transitions ----------
  const canNextStep1 = eventDate && eventZip.trim().length >= 3 && partySize > 0;
  const canNextStep2 = !!eventType;  // cuisine is optional
  const canNextStep3 = selectedIds.size > 0;
  const canSubmit = name.trim() && email.trim() && message.trim().length >= 10 && selectedIds.size > 0;

  // ---------- Fetch matches when we enter step 3 ----------
  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    setMatchLoading(true);
    const cuisineParam = anyCuisine || cuisines.length === 0 ? "" : cuisines.join(",");
    api
      .get("/find-trucks/match", {
        params: { event_zip: eventZip, party_size: partySize, cuisine: cuisineParam || undefined },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.matches || [];
        const also = res.data?.also_nearby || [];
        setMatches(list);
        setAlsoNearby(also);
        setShowAlsoNearby(false);
        setGeocoded(res.data?.geocoded !== false);
        // Pre-select top 5 (or all, whichever is fewer)
        setSelectedIds(new Set(list.slice(0, 5).map((m) => m.truck_id)));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(formatApiError(err));
        setMatches([]);
        setAlsoNearby([]);
      })
      .finally(() => {
        if (!cancelled) setMatchLoading(false);
      });
    return () => { cancelled = true; };
  }, [step]);

  // ---------- Cuisine chip toggle ----------
  function toggleCuisine(tag) {
    setAnyCuisine(false);
    setCuisines((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }
  function toggleAnyCuisine() {
    setAnyCuisine((prev) => {
      const next = !prev;
      if (next) setCuisines([]);
      return next;
    });
  }

  function toggleTruck(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 10) {
          toast.message("Max 10 trucks per inquiry — uncheck one to add another.");
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  }

  const selectedTruckNames = useMemo(() => {
    const all = [...matches, ...alsoNearby];
    return all.filter((m) => selectedIds.has(m.truck_id)).map((m) => m.truck_name);
  }, [matches, alsoNearby, selectedIds]);

  // Visible matches respect the "Pro only" toggle. We DON'T filter on
  // the API call — the host can switch between "all" and "Pro only"
  // without re-fetching, which is faster + keeps the same per-request
  // randomisation in play.
  const visibleMatches = useMemo(
    () => (proOnly ? matches.filter((m) => m.is_pro) : matches),
    [matches, proOnly],
  );
  const visibleAlsoNearby = useMemo(
    () => (proOnly ? alsoNearby.filter((m) => m.is_pro) : alsoNearby),
    [alsoNearby, proOnly],
  );
  const proCount = useMemo(() => matches.filter((m) => m.is_pro).length, [matches]);

  function toggleProOnly() {
    setProOnly((prev) => {
      const next = !prev;
      // If turning ON, deselect any free trucks currently in selection
      // so the summary on step 4 stays honest.
      if (next) {
        setSelectedIds((sel) => {
          const proIds = new Set(
            [...matches, ...alsoNearby].filter((m) => m.is_pro).map((m) => m.truck_id),
          );
          return new Set([...sel].filter((id) => proIds.has(id)));
        });
      }
      return next;
    });
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post("/event-inquiries", {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim() || null,
        event_date_start: eventDate,
        event_zip: eventZip.trim(),
        party_size: Number(partySize),
        cuisine_preferences: anyCuisine ? [] : cuisines,
        event_type: eventType,
        budget_per_head: budget ? Number(budget) : null,
        message: message.trim(),
        selected_truck_ids: Array.from(selectedIds),
        website,  // honeypot — empty for real users
      });
      setSubmitted({
        ...res.data,
        sent_truck_names: selectedTruckNames,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // -------------------- Submitted (confirmation) view --------------------
  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20" data-testid="find-a-truck-confirmation">
          <div className="text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-primary/10 grid place-items-center">
              <CheckCircle weight="fill" className="h-10 w-10 text-primary" />
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Sent to {submitted.sent_count} truck{submitted.sent_count === 1 ? "" : "s"}!
            </h1>
            <p className="mt-3 text-base text-muted-foreground">
              Most trucks reply within 48 hours. We've also emailed you a confirmation —
              their responses will land straight in your inbox at <strong>{email}</strong>.
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
              We sent your inquiry to:
            </div>
            <ul className="space-y-2">
              {submitted.sent_truck_names.map((n) => (
                <li key={n} className="flex items-center gap-2 text-sm">
                  <Storefront weight="duotone" className="h-4 w-4 text-primary flex-none" />
                  {n}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link to="/map">
              <Button variant="outline" data-testid="confirm-back-to-map">
                Browse trucks on the map
              </Button>
            </Link>
            {submitted.view_url && (
              <a href={submitted.view_url} target="_blank" rel="noreferrer">
                <Button data-testid="confirm-view-inquiry">View my inquiry →</Button>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------------------- Main multi-step form --------------------
  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Book a Food Truck for Your Event | TruckTrackr"
        description="Tell us about your party, wedding, or company event and we'll connect you with available local food trucks. One request, free for hosts."
        canonical="https://thetrucktrackr.com/find-a-truck"
      />
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-testid="find-a-truck-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
            <Sparkle weight="fill" className="h-3 w-3" />
            Event Inquiry · Free for hosts
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Find a food truck for your event
          </h1>
          <p className="mt-2 text-muted-foreground text-sm sm:text-base">
            Get quotes from Central PA's best food trucks in minutes.
          </p>
          {/* Standalone share — a shareable link to THIS page that
              previews beautifully on iMessage, FB, etc. Host with the
              co-organizer? Post in a Discord? Send to the church
              admin? One tap. */}
          <ShareInquiryButton />
        </div>

        <StepHeader step={step} />

        {/* ────────── STEP 1 — When + where ────────── */}
        {step === 1 && (
          <div className="space-y-5" data-testid="step-1">
            <div className="space-y-2">
              <Label htmlFor="event-date" className="flex items-center gap-1.5">
                <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
                Event date
              </Label>
              <Input
                id="event-date"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-zip" className="flex items-center gap-1.5">
                <MapPin weight="duotone" className="h-4 w-4 text-primary" />
                ZIP code or city
              </Label>
              <Input
                id="event-zip"
                type="text"
                value={eventZip}
                onChange={(e) => setEventZip(e.target.value)}
                placeholder="17601 or Lancaster, PA"
                data-testid="event-zip-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="party-size">Number of guests</Label>
              <Input
                id="party-size"
                type="number"
                value={partySize}
                onChange={(e) => setPartySize(Number(e.target.value || 0))}
                min={1}
                max={10000}
                data-testid="party-size-input"
              />
            </div>
          </div>
        )}

        {/* ────────── STEP 2 — Event type + cuisine ────────── */}
        {step === 2 && (
          <div className="space-y-5" data-testid="step-2">
            <div className="space-y-2">
              <Label>Event type</Label>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setEventType(t.value)}
                    className={
                      "px-3 py-1.5 rounded-full text-sm border transition " +
                      (eventType === t.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/40")
                    }
                    data-testid={`event-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cuisine preferences (optional)</Label>
              <button
                type="button"
                onClick={toggleAnyCuisine}
                className={
                  "w-full rounded-xl border-2 p-3 text-left transition " +
                  (anyCuisine
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:border-primary/40")
                }
                data-testid="any-cuisine-toggle"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    readOnly
                    checked={anyCuisine}
                    className="h-4 w-4 accent-primary pointer-events-none"
                  />
                  <span className="font-semibold text-sm">Any cuisine — surprise me 🎲</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-0.5">
                  Match me with the best-fit trucks regardless of food type.
                </p>
              </button>
              <div className="flex flex-wrap gap-2 mt-2">
                {COMMON_CUISINES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    disabled={anyCuisine}
                    onClick={() => toggleCuisine(c)}
                    className={
                      "px-3 py-1.5 rounded-full text-sm border transition disabled:opacity-40 " +
                      (cuisines.includes(c) && !anyCuisine
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/40")
                    }
                    data-testid={`cuisine-${c.replace(/\s+/g, "-")}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget">Budget per head (optional)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="budget"
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="25"
                  className="pl-7"
                  data-testid="budget-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* ────────── STEP 3 — Pick trucks ────────── */}
        {step === 3 && (
          <div className="space-y-3" data-testid="step-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm">
                <span className="font-semibold">
                  {matchLoading
                    ? "Finding matches…"
                    : `${visibleMatches.length} top match${visibleMatches.length === 1 ? "" : "es"}`}
                </span>
                <span className="text-muted-foreground"> near {eventZip}</span>
                {!matchLoading && visibleAlsoNearby.length > 0 && (
                  <span className="text-muted-foreground"> · {visibleAlsoNearby.length} more nearby</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{selectedIds.size}</span>/10 selected
              </div>
            </div>

            {/* Pro-only filter — surfaced when ≥1 Pro truck is in the result
                set AND there's at least one non-Pro to actually filter out.
                Otherwise the toggle is a no-op and we hide it to reduce noise. */}
            {!matchLoading && proCount > 0 && proCount < matches.length && (
              <button
                type="button"
                onClick={toggleProOnly}
                className={
                  "w-full flex items-center justify-between gap-3 rounded-xl border-2 px-3 py-2.5 transition " +
                  (proOnly
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:border-primary/40")
                }
                data-testid="pro-only-toggle"
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={
                      "h-5 w-9 rounded-full transition relative " +
                      (proOnly ? "bg-primary" : "bg-muted")
                    }
                  >
                    <div
                      className={
                        "absolute top-0.5 h-4 w-4 rounded-full bg-card shadow transition " +
                        (proOnly ? "left-[18px]" : "left-0.5")
                      }
                    />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold">Show Pro trucks only</div>
                    <div className="text-[11px] text-muted-foreground">
                      Verified businesses · ideal for weddings, corporate, larger events
                    </div>
                  </div>
                </div>
                <div className="text-[11px] font-mono text-muted-foreground flex-none whitespace-nowrap">
                  {proCount}/{matches.length} Pro
                </div>
              </button>
            )}

            {!geocoded && (
              <div className="rounded-md border border-amber-300/40 bg-amber-50/50 dark:bg-amber-950/20 p-3 text-xs" data-testid="geocode-warning">
                We couldn't pinpoint <strong>{eventZip}</strong> — double-check the ZIP code.
                A valid location is needed to match trucks near your event.
              </div>
            )}

            {matchLoading ? (
              <div className="py-12 text-center text-muted-foreground">
                <CircleNotch weight="bold" className="h-6 w-6 mx-auto animate-spin text-primary" />
                <div className="mt-2 text-sm">Searching nearby trucks…</div>
              </div>
            ) : visibleMatches.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground rounded-2xl border border-dashed border-border">
                <Storefront weight="duotone" className="h-10 w-10 mx-auto opacity-60" />
                <p className="mt-3 text-sm max-w-sm mx-auto" data-testid="no-matches-message">
                  {proOnly && matches.length > 0
                    ? "No Pro trucks in this match. Toggle off to see all matches."
                    : geocoded
                      ? "TruckTrackr doesn't have food trucks within 50 miles of your event yet — we're adding new areas fast, so check back soon."
                      : "No matches yet. Double-check your ZIP code above."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {visibleMatches.map((m) => (
                  <TruckCard
                    key={m.truck_id}
                    truck={m}
                    checked={selectedIds.has(m.truck_id)}
                    onToggle={toggleTruck}
                  />
                ))}

                {visibleAlsoNearby.length > 0 && (
                  <div className="pt-3">
                    {!showAlsoNearby ? (
                      <button
                        type="button"
                        onClick={() => setShowAlsoNearby(true)}
                        className="w-full rounded-xl border-2 border-dashed border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition px-3 py-3 text-sm font-semibold text-foreground"
                        data-testid="show-also-nearby-btn"
                      >
                        See {visibleAlsoNearby.length} more nearby truck{visibleAlsoNearby.length === 1 ? "" : "s"}
                        <div className="text-[11px] font-normal text-muted-foreground mt-0.5">
                          Smaller / less-complete profiles in your 50-mile radius
                        </div>
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2 pb-2">
                          <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                            Also nearby · {visibleAlsoNearby.length}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowAlsoNearby(false)}
                            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                            data-testid="hide-also-nearby-btn"
                          >
                            Hide
                          </button>
                        </div>
                        <div className="space-y-2">
                          {visibleAlsoNearby.map((m) => (
                            <TruckCard
                              key={m.truck_id}
                              truck={m}
                              checked={selectedIds.has(m.truck_id)}
                              onToggle={toggleTruck}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ────────── STEP 4 — Host contact + message ────────── */}
        {step === 4 && (
          <div className="space-y-5" data-testid="step-4">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Sending to <strong>{selectedTruckNames.length} truck{selectedTruckNames.length === 1 ? "" : "s"}</strong>:{" "}
              {selectedTruckNames.slice(0, 3).join(", ")}
              {selectedTruckNames.length > 3 && ` +${selectedTruckNames.length - 3} more`}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sarah Johnson"
                data-testid="host-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sarah@example.com"
                data-testid="host-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(717) 555-1234"
                data-testid="host-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Tell the trucks about your event</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="We're getting married July 11th at the Lancaster Country Club. Looking for tacos for ~80 guests, dinner 6-8pm…"
                data-testid="host-message-input"
              />
              <div className="text-[11px] text-muted-foreground">
                {message.length}/2000 · at least 10 characters
              </div>
            </div>

            {/* Honeypot — real users never see this */}
            <div className="hidden" aria-hidden="true">
              <label>
                Website (do not fill)
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>
          </div>
        )}

        {/* ────────── Footer: back / next / submit ────────── */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || submitting}
            data-testid="step-back-btn"
          >
            <CaretLeft weight="bold" className="h-4 w-4 mr-1" />
            Back
          </Button>

          {step < 4 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={
                (step === 1 && !canNextStep1) ||
                (step === 2 && !canNextStep2) ||
                (step === 3 && !canNextStep3)
              }
              data-testid="step-next-btn"
            >
              Next
              <CaretRight weight="bold" className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              data-testid="step-submit-btn"
            >
              {submitting ? (
                <>
                  <CircleNotch weight="bold" className="h-4 w-4 mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <PaperPlaneTilt weight="bold" className="h-4 w-4 mr-1.5" />
                  Send to {selectedIds.size} truck{selectedIds.size === 1 ? "" : "s"}
                </>
              )}
            </Button>
          )}
        </div>

        <div className="mt-10 text-center text-xs text-muted-foreground">
          Trusted by Central PA's food truck network · Your email stays private
        </div>
      </div>
    </div>
  );
}
